import { convertValueToString } from './common';
import { ParamsObject } from './types/index';

export default class QueryStringHandler {
    private url: URL;

    private params: URLSearchParams;

    private isBrowserEnvironment: boolean;

    /**
     * Class constructor
     * @param urlString The URL string to work with. Defaults to window.location.href.
     */
    constructor(urlString?: string) {
        this.isBrowserEnvironment = typeof window !== 'undefined' && typeof window.history !== 'undefined';

        let initialUrl = urlString;

        if (!initialUrl && this.isBrowserEnvironment) {
            initialUrl = window.location.href;
        } else if (!initialUrl) {
            initialUrl = 'https://www.no.url/';
        }

        try {
            this.url = new URL(initialUrl!);
            this.params = this.url.searchParams;
        } catch {
            throw new Error(`Invalid URL provided: ${initialUrl}`);
        }
    }

    /**
     * Checks if a query string parameter exists, and optionally checking for a specific value.
     * @param key The query parameter key to check for.
     * @param value Optional. The specific value to check for under that key.
     * @returns True if the key exists (..and optionally has the specified value) or false
     */
    public has(key: string, value?: string): boolean {
        if (value === undefined) {
            return this.params.has(key);
        }
        return this.params.has(key) && this.params.get(key) === value;
    }

    /**
     * Gets the value of a specific query parameter key.
     * @param key The query parameter key to retrieve.
     * @returns The parameter's value as a string, or null if the key doesn't exist.
     */
    public get(key: string): string | null {
        return this.params.get(key);
    }

    /**
     * Gets all values associated with a specific query parameter key.
     * @param key The query parameter key to retrieve.
     * @returns An array of string values. Returns an empty array if the key doesn't exist.
     */
    public getAll(key: string): string[] {
        return this.params.getAll(key);
    }

    /**
     * Gets all query parameters as object.
     * @returns An object mapping keys to string or string array values.
     */
    public getAllAsObject(): { [key: string]: string | string[] } {
        const obj: { [key: string]: string | string[] } = {};
        const seenKeys = new Set<string>();

        for (const key of this.params.keys()) {
            if (!seenKeys.has(key)) {
                const values = this.params.getAll(key);
                obj[key] = values.length > 1 ? values : values[0];
                seenKeys.add(key);
            }
        }
        return obj;
    }

    /**
     * Sets a new value for a key (This will replace existing value)
     * @param key The query parameter key to set.
     * @param value The value to set for the key.
     * @returns this instance
     */
    public set(key: string, value: string): this {
        this.params.set(key, value);
        return this;
    }

    /**
     * Replaces ALL existing query parameters with a new set from the provided object.
     * @param params The object containing the new key-value pairs.
     * @returns this instance
     */
    public setAll(params: ParamsObject): this {
        this.clear();
        return this.update(params);
    }

    /**
     * Adds or updates query parameters from object without clearing existing ones.
     * @param params The object containing the key-value pairs to update or add.
     * @returns this instance
     */
    public update(params: ParamsObject): this {
        for (const [key, value] of Object.entries(params)) {
            const stringifiedValue = convertValueToString(value);

            if (stringifiedValue !== undefined) {
                if (Array.isArray(stringifiedValue)) {
                    this.params.delete(key);
                    stringifiedValue.forEach((v) => this.params.append(key, v));
                } else {
                    this.params.set(key, stringifiedValue);
                }
            }
        }
        return this;
    }

    /**
     * Add a new key-value pair.
     * @param key The query parameter key to add.
     * @param value The value to add for the key.
     * @returns this instance
     */
    public append(key: string, value: string): this {
        this.params.append(key, value);
        return this;
    }

    /**
     * Removes a specific key and all its associated values from the query string.
     * @param key The query parameter key to remove.
     * @returns this instance
     */
    public delete(key: string): this {
        this.params.delete(key);
        return this;
    }

    /**
     * Removes ONLY a specific value for a given key.
     * @param key The parameter key.
     * @param value The specific value to remove.
     * @returns this instance
     */
    public deleteByValue(key: string, value: string): this {
        const values = this.params.getAll(key);

        if (values.length === 0) {
            return this;
        }

        const newValues = values.filter((v) => v !== value);

        if (newValues.length === 0) {
            this.params.delete(key);
            return this;
        }

        this.params.delete(key);
        newValues.forEach((v) => this.params.append(key, v));

        return this;
    }

    /**
     * Clears all existing query parameters.
     * @returns this instance
     */
    public clear(): this {
        for (const key of Array.from(this.params.keys())) {
            this.params.delete(key);
        }
        return this;
    }

    /**
     * Update the browser's URL
     */
    public pushState(state: unknown = null, title: string = document.title): void {
        if (!this.isBrowserEnvironment) {
            console.warn('pushState() called outside of a browser environment. URL was not updated.');
            return;
        }
        const newUrl = this.getFullUrl();

        window.history.pushState(state as any, title, newUrl);
        this.url = new URL(newUrl);
    }

    /**
     * Replace the browser's URL
     */
    public replaceState(state: unknown = null, title: string = document.title): void {
        if (!this.isBrowserEnvironment) {
            console.warn('replaceState() called outside of a browser environment. URL was not updated.');
            return;
        }
        const newUrl = this.getFullUrl();

        window.history.replaceState(state as any, title, newUrl);
        this.url = new URL(newUrl);
    }

    /**
     * Set path
     * @param newPath The new path string
     * @returns this instance
     */
    public setPath(newPath: string): this {
        this.url.pathname = newPath.startsWith('/') ? newPath : `/${newPath}`;
        return this;
    }

    /**
     * Get the current path of the URL
     * @returns Current path
     */
    public getPath(): string {
        return this.url.pathname;
    }

    /**
     * Get the full URL
     * @returns Full modified URL WITH query string
     */
    public getFullUrl(): string {
        this.url.search = this.params.toString();
        return this.url.toString();
    }

    /**
     * Get the query string
     * @returns Modified query string starting with ?
     */
    public getQueryString(): string {
        return this.params.toString() ? `?${this.params.toString()}` : '';
    }
}
